import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import PuffLoader from 'react-spinners/PuffLoader';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AutocompleteSelectFilter from '../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import { getMonthName } from '../ReportingFollowUp/reportingFollowUpConstants';
import SalariesService, { SALARY_SETTINGS_SAVED_EVENT, SALARY_SOURCE } from '../../Services/salaries.service';
import CodeSourcePicker from '../Widgets/CodeSourcePicker/CodeSourcePicker.jsx';
import useCodeSourceOptions from '../Widgets/CodeSourcePicker/useCodeSourceOptions';
import { beautifyNumber } from '../../Utils/FormatNumber';
import followUpStyles from '../ReportingFollowUp/ReportingFollowUp.module.scss';
import niStyles from '../NationalInsurance/NationalInsurance.module.scss';
import styles from './Salary.module.scss';

export const SALARY_CATEGORIES = [
    { key: 'salaries', labelKey: 'SalarySalaries' },
    { key: 'socialBenefits', labelKey: 'SalarySocialBenefits' },
];

const STATUS = {
    FILLED: 'filled',
    MISSING: 'missing',
    CURRENT: 'current',
    FUTURE: 'future',
    INACTIVE: 'inactive',
    NOT_CONFIGURED: 'notConfigured',
};
const STATUS_LABEL_KEYS = {
    [STATUS.FILLED]: 'SalaryStatusFilled',
    [STATUS.MISSING]: 'SalaryStatusMissing',
    [STATUS.CURRENT]: 'SalaryStatusCurrent',
    [STATUS.FUTURE]: 'SalaryStatusFuture',
    [STATUS.INACTIVE]: 'SalaryStatusInactive',
    [STATUS.NOT_CONFIGURED]: 'SalaryStatusNotConfigured',
};
const STATUS_CLASS = {
    [STATUS.FILLED]: styles.statusFilled,
    [STATUS.MISSING]: styles.statusMissing,
    [STATUS.CURRENT]: styles.statusCurrent,
    [STATUS.FUTURE]: styles.statusMuted,
    [STATUS.INACTIVE]: styles.statusMuted,
    [STATUS.NOT_CONFIGURED]: styles.statusMuted,
};
const MAX_YEARS_SPAN = 6;

const emptyCategory = () => ({ sourceType: SALARY_SOURCE.ACCOUNT_CARDS, codes: [] });
const toDraft = (settings) => ({
    salaries: { sourceType: settings?.salaries?.sourceType || SALARY_SOURCE.ACCOUNT_CARDS, codes: settings?.salaries?.codes || [] },
    socialBenefits: { sourceType: settings?.socialBenefits?.sourceType || SALARY_SOURCE.ACCOUNT_CARDS, codes: settings?.socialBenefits?.codes || [] },
    filterByDocumentDate: !!settings?.filterByDocumentDate,
});
const formatAmount = (value) => `${beautifyNumber(value || 0)} ₪`;

const SalaryModal = ({ open, onClose, client, token, startYear, endYear, periodStart, periodEnd, canEdit, onSaved }) => {
    const { t, i18n } = useTranslation();
    const dir = i18n.dir();
    const language = i18n.language;
    const caseId = client?.caseId;
    const accountantId = client?.accountantId;

    const [fromYear, setFromYear] = useState(startYear);
    const [toYear, setToYear] = useState(endYear);
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [draft, setDraft] = useState(() => ({ salaries: emptyCategory(), socialBenefits: emptyCategory(), filterByDocumentDate: false }));
    const [savedSnapshot, setSavedSnapshot] = useState('');
    const [saving, setSaving] = useState(false);
    const requestIdRef = useRef(0);
    const isDirtyRef = useRef(false);

    useEffect(() => {
        if (!open) return;
        setSavedSnapshot('');
        setFromYear(startYear);
        setToYear(endYear);
        // Only re-seed when the modal opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const optionsBySource = useCodeSourceOptions({ enabled: open, token, caseId, accountantId });

    const applySettings = useCallback((settings) => {
        const next = toDraft(settings);
        setDraft(next);
        setSavedSnapshot(JSON.stringify(next));
    }, []);

    const load = useCallback(async () => {
        if (!caseId || !token || !fromYear || !toYear) return;
        const requestId = ++requestIdRef.current;
        setLoading(true);
        try {
            const data = await SalariesService.getOverview({
                caseId,
                startYear: Math.min(fromYear, toYear),
                endYear: Math.max(fromYear, toYear),
                periodStart,
                periodEnd,
            }, token);
            if (requestId !== requestIdRef.current) return;
            setOverview(data);
            if (!isDirtyRef.current) applySettings(data?.settings);
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            console.error('Error loading salary overview:', error);
            toast.error(t('SalaryLoadFailed'));
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    }, [caseId, token, fromYear, toYear, periodStart, periodEnd, applySettings, t]);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    const yearOptions = useMemo(() => {
        const current = new Date().getFullYear();
        return Array.from({ length: 10 }, (_, i) => current + 1 - i).map((y) => ({ value: y, label: String(y) }));
    }, []);

    const isDirty = savedSnapshot !== '' && savedSnapshot !== JSON.stringify(draft);
    isDirtyRef.current = isDirty;

    const setCategory = (key, patch) => setDraft((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

    const handleYearChange = (setter, otherYear, isFrom) => (e, option) => {
        if (!option) return;
        const nextFrom = isFrom ? option.value : otherYear;
        const nextTo = isFrom ? otherYear : option.value;
        if (Math.abs(nextTo - nextFrom) >= MAX_YEARS_SPAN) {
            toast.info(t('SalaryYearsLimit', { count: MAX_YEARS_SPAN }));
            return;
        }
        setter(option.value);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const saved = await SalariesService.saveSettings({ caseId, ...draft }, token);
            applySettings(saved);
            isDirtyRef.current = false;
            toast.success(t('SalarySaved'));
            window.dispatchEvent(new CustomEvent(SALARY_SETTINGS_SAVED_EVENT, { detail: { caseId } }));
            if (typeof onSaved === 'function') onSaved();
            await load();
        } catch (error) {
            console.error('Error saving salary settings:', error);
            toast.error(t('SalarySaveFailed'));
        } finally {
            setSaving(false);
        }
    };

    // ------------------------------------------------------------------ render

    const renderSettingsCard = () => (
        <div className={niStyles.card}>
            <div className={niStyles.cardHeader}>
                <span className={niStyles.cardTitle}>{t('SalarySourcesTitle')}</span>
                <ToggleButtonGroup
                    className={niStyles.scopeToggle}
                    size="small"
                    exclusive
                    value={draft.filterByDocumentDate ? 'document' : 'registration'}
                    onChange={(e, value) => value && setDraft((prev) => ({ ...prev, filterByDocumentDate: value === 'document' }))}
                    disabled={!canEdit || saving}
                >
                    <ToggleButton value="registration">{t('FilterByRegisterMonthDate')}</ToggleButton>
                    <ToggleButton value="document">{t('FilterByDocumentDate')}</ToggleButton>
                </ToggleButtonGroup>
            </div>
            <div className={niStyles.cardBody}>
                {SALARY_CATEGORIES.map(({ key, labelKey }) => (
                    <CodeSourcePicker
                        key={key}
                        label={t(labelKey)}
                        value={draft[key]}
                        onChange={(next) => setCategory(key, next)}
                        optionsBySource={optionsBySource}
                        disabled={!canEdit || saving}
                    />
                ))}
                <div className={niStyles.previewActions}>
                    <Button
                        className={followUpStyles.save}
                        variant="contained"
                        disableElevation
                        onClick={handleSave}
                        disabled={!canEdit || !isDirty || saving}
                    >
                        {saving ? t('Loading') : t('SalarySaveSources')}
                    </Button>
                </div>
            </div>
        </div>
    );

    const renderSummaryChip = ({ key, labelKey }) => {
        const summary = overview?.[key];
        if (!summary?.isConfigured) {
            return <span key={key} className={niStyles.chip}>{t(labelKey)}: <strong>{t('SalaryStatusNotConfigured')}</strong></span>;
        }
        const missing = summary.missingMonths?.length || 0;
        return missing > 0 ? (
            <span key={key} className={`${niStyles.chip} ${niStyles.chipWarning}`}>
                {t(labelKey)}: <strong>{t('SalaryMissingMonthsCount', { count: missing })}</strong>
            </span>
        ) : (
            <span key={key} className={`${niStyles.chip} ${niStyles.chipSuccess}`}>
                {t(labelKey)}: <strong>{t('SalaryAllMonthsFilled')}</strong>
            </span>
        );
    };

    const renderCell = (cell) => {
        const status = cell?.status || STATUS.NOT_CONFIGURED;
        const hasData = status === STATUS.FILLED;
        return (
            <span className={`${styles.monthCell} ${STATUS_CLASS[status] || ''}`}>
                <span className={styles.monthAmount}>{hasData ? formatAmount(cell.amount) : '—'}</span>
                <span className={styles.statusPill}>
                    {status === STATUS.FILLED ? <CheckCircleRoundedIcon fontSize="inherit" /> : null}
                    {status === STATUS.MISSING ? <WarningAmberRoundedIcon fontSize="inherit" /> : null}
                    {t(STATUS_LABEL_KEYS[status])}
                </span>
            </span>
        );
    };

    const monthsByYear = useMemo(() => {
        const groups = new Map();
        (overview?.months || []).forEach((row) => {
            if (!groups.has(row.year)) groups.set(row.year, []);
            groups.get(row.year).push(row);
        });
        return Array.from(groups.entries());
    }, [overview]);

    const renderMonthsCard = () => (
        <div className={niStyles.card}>
            <div className={niStyles.cardHeader}>
                <span className={niStyles.cardTitle}>{t('SalaryMonthsTitle')}</span>
                <div className={niStyles.meta}>{SALARY_CATEGORIES.map(renderSummaryChip)}</div>
            </div>
            <div className={niStyles.cardBody}>
                {loading && !overview ? (
                    <div className={niStyles.loader}><PuffLoader size={60} loading /></div>
                ) : (
                    <div style={{ opacity: loading ? 0.5 : 1 }}>
                        <div className={niStyles.notice}>{t('SalaryMonthsHint')}</div>
                        {monthsByYear.map(([year, rows]) => (
                            <div key={year} className={styles.yearBlock}>
                                <div className={styles.yearTitle}>{year}</div>
                                <div className={styles.monthsGrid}>
                                    <span className={niStyles.columnHead}>{t('NIMonth')}</span>
                                    {SALARY_CATEGORIES.map(({ key, labelKey }) => (
                                        <span key={key} className={niStyles.columnHead}>{t(labelKey)}</span>
                                    ))}
                                    {rows.map((row) => (
                                        <React.Fragment key={`${row.year}-${row.month}`}>
                                            <span className={niStyles.monthLabel}>
                                                {getMonthName(row.month, language)}
                                                <span className={styles.monthNumber}>{String(row.month).padStart(2, '0')}/{row.year}</span>
                                            </span>
                                            {renderCell(row.salaries)}
                                            {renderCell(row.socialBenefits)}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    const renderYearSelect = (value, onChange, label) => (
        <div className={niStyles.yearField}>
            <AutocompleteSelectFilter
                options={yearOptions}
                selectedValues={yearOptions.find((o) => o.value === value) || { value, label: String(value) }}
                onChange={onChange}
                placeholder={label}
                isMultiple={false}
                direction={dir}
                portalMenu
            />
        </div>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            dir={dir}
            PaperProps={{
                className: `${followUpStyles.paper} ${niStyles.paper}`,
                sx: { maxWidth: 1120, width: 'min(1120px, calc(100vw - 32px))' },
            }}
            BackdropProps={{ sx: { background: 'rgba(20, 30, 43, 0.45)', backdropFilter: 'blur(6px)' } }}
        >
            <DialogTitle className={followUpStyles.header}>
                <span>{t('SalaryTitle')}</span>
                <div className={followUpStyles.headerEnd}>
                    {client ? (
                        <span className={followUpStyles.headerClient}>{client.name} · {client.caseId}</span>
                    ) : null}
                    {renderYearSelect(fromYear, handleYearChange(setFromYear, toYear, true), t('StartYear'))}
                    <span className={styles.yearsDivider}>–</span>
                    {renderYearSelect(toYear, handleYearChange(setToYear, fromYear, false), t('EndYear'))}
                </div>
            </DialogTitle>

            <DialogContent className={followUpStyles.scroll}>
                <div className={niStyles.body}>
                    {renderSettingsCard()}
                    {renderMonthsCard()}
                </div>
            </DialogContent>

            <DialogActions className={followUpStyles.actions}>
                <Button className={followUpStyles.cancel} variant="contained" disableElevation onClick={onClose}>
                    {t('close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

SalaryModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    client: PropTypes.object,
    token: PropTypes.string,
    startYear: PropTypes.number,
    endYear: PropTypes.number,
    periodStart: PropTypes.string,
    periodEnd: PropTypes.string,
    canEdit: PropTypes.bool,
    onSaved: PropTypes.func,
};

export default SalaryModal;
